import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Select, SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";
import { getLanguagesArray } from "~/store/selectors/selector.languages";

const SelectLanguage = ({
  target,
  selected,
  onChange,
  placeholder,
  noResultText,
  targetClassName,
  options,
  ...rest
}) => {
  const { t } = useTranslation();
  const languages = useSelector(getLanguagesArray);

  const languagesOptions = useMemo(() => {
    if (options && options.length > 0) {
      return options;
    }
    return languages;
  }, [languages, options]);

  const languageName = useMemo(() => {
    if (selected > 0 && languagesOptions.length > 0) {
      const language = languagesOptions.find((language) => language.id === selected);
      if (language) {
        return language.name;
      }
    }
    return undefined;
  }, [languagesOptions, selected]);

  const trigger = useMemo(() => {
    if (target) return target;

    return (
      <SelectTrigger
        data-testid="select-language"
        className={targetClassName}
      >
        {languageName ?? placeholder}
      </SelectTrigger>
    );
  }, [languageName, placeholder, target, targetClassName]);

  return (
    <Select
      target={trigger}
      options={languagesOptions}
      selected={selected}
      onChange={onChange}
      placeholder={placeholder ? placeholder : t("Choose a language")}
      noResultText={noResultText ? noResultText : t("No result")}
      disabledInternalSort
      keepSameOptionsOrder
      forceCloseMenu
      {...rest}
    />
  );
};

SelectLanguage.propTypes = {
  target: PropTypes.element,
  selected: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  noResultText: PropTypes.string,
  targetClassName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

SelectLanguage.defaultProps = {
  placeholder: "",
  options: [],
};

export default SelectLanguage;
